/**
 * RBAC API client
 */

import { getAPIClient } from './client';

export interface Role {
  id: string;
  name: string;
  display_name: string;
  scope: string;
  description?: string;
  is_system_role: boolean;
  created_at: string;
  permission_count?: number;
}

export interface RoleDetail extends Role {
  permissions: Array<{
    id: string;
    name: string;
    display_name: string;
    resource_type: string;
    action: string;
  }>;
}

export interface Permission {
  id: string;
  name: string;
  display_name: string;
  resource_type: string;
  action: string;
  description: string;
  created_at: string;
}

export interface UserPermissions {
  user_id: string;
  tenant_id?: string;
  project_id?: string;
  permissions: string[];
  roles: Array<{
    scope: string;
    role_id: string;
    role_name: string;
    display_name: string;
  }>;
}

export interface CreateRoleRequest {
  name: string;
  display_name: string;
  scope: string;
  description?: string;
  permission_ids?: string[];
}

/**
 * List roles
 */
export async function listRoles(params?: {
  scope?: string;
  tenant_id?: string;
  project_id?: string;
}): Promise<Role[]> {
  const client = getAPIClient();
  return client.get<Role[]>('/rbac/roles', { params });
}

/**
 * Get role details
 */
export async function getRole(roleId: string): Promise<RoleDetail> {
  const client = getAPIClient();
  return client.get<RoleDetail>(`/rbac/roles/${roleId}`);
}

/**
 * Create role
 */
export async function createRole(request: CreateRoleRequest): Promise<Role> {
  const client = getAPIClient();
  return client.post<Role>('/rbac/roles', request);
}

/**
 * Update role
 */
export async function updateRole(roleId: string, request: Partial<CreateRoleRequest>): Promise<Role> {
  const client = getAPIClient();
  return client.patch<Role>(`/rbac/roles/${roleId}`, request);
}

/**
 * List permissions
 */
export async function listPermissions(params?: {
  resource_type?: string;
  action?: string;
}): Promise<Permission[]> {
  const client = getAPIClient();
  return client.get<Permission[]>('/rbac/permissions', { params });
}

/**
 * Get current user's permissions
 */
export async function getMyPermissions(params?: {
  tenant_id?: string;
  project_id?: string;
}): Promise<UserPermissions> {
  const client = getAPIClient();
  return client.get<UserPermissions>('/rbac/permissions/me', { params });
}

/**
 * Get user's permissions
 */
export async function getUserPermissions(userId: string, params?: {
  tenant_id?: string;
  project_id?: string;
}): Promise<UserPermissions> {
  const client = getAPIClient();
  return client.get<UserPermissions>(`/rbac/permissions/users/${userId}`, { params });
}











